// src/pages/Jobs/JobStateMapPage.tsx - FIXED VERSION
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button } from 'reactstrap';
import Header from '../Home/Header';
import Footer from '../Home/Footer';
import SEOTags from '../Listing/SEOTags';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import InteractiveUSMap, { MapRegion } from '../../Components/InteractiveUSMap';

const JobStateMapPage: React.FC = () => {
  const { state } = useParams<{ state?: string }>();
  const navigate = useNavigate();

  // Convert state code to full name for display
  const getStateName = (stateCode: string): string => {
    const stateNames: Record<string, string> = {
      'al': 'Alabama', 'ak': 'Alaska', 'az': 'Arizona', 'ar': 'Arkansas', 'ca': 'California',
      'co': 'Colorado', 'ct': 'Connecticut', 'de': 'Delaware', 'fl': 'Florida', 'ga': 'Georgia',
      'hi': 'Hawaii', 'id': 'Idaho', 'il': 'Illinois', 'in': 'Indiana', 'ia': 'Iowa',
      'ks': 'Kansas', 'ky': 'Kentucky', 'la': 'Louisiana', 'me': 'Maine', 'md': 'Maryland',
      'ma': 'Massachusetts', 'mi': 'Michigan', 'mn': 'Minnesota', 'ms': 'Mississippi', 'mo': 'Missouri',
      'mt': 'Montana', 'ne': 'Nebraska', 'nv': 'Nevada', 'nh': 'New Hampshire', 'nj': 'New Jersey',
      'nm': 'New Mexico', 'ny': 'New York', 'nc': 'North Carolina', 'nd': 'North Dakota', 'oh': 'Ohio',
      'ok': 'Oklahoma', 'or': 'Oregon', 'pa': 'Pennsylvania', 'ri': 'Rhode Island', 'sc': 'South Carolina',
      'sd': 'South Dakota', 'tn': 'Tennessee', 'tx': 'Texas', 'ut': 'Utah', 'vt': 'Vermont',
      'va': 'Virginia', 'wa': 'Washington', 'wv': 'West Virginia', 'wi': 'Wisconsin', 'wy': 'Wyoming'
    };
    
    return stateNames[stateCode?.toLowerCase()] || stateCode?.toUpperCase() || '';
  };

  const displayState = getStateName(state || '');

  const handleRegionClick = (region: MapRegion) => {
    if (region.raw_name) {
      const citySlug = region.raw_name.toLowerCase().replace(/[^a-z0-9]/g, '-');
      navigate(`/jobs/${state?.toLowerCase()}/${citySlug}`);
    }
  };

  const handleBackToMap = () => {
    navigate('/jobs');
  };

  if (!state) {
    return (
      <React.Fragment>
        <Header />
        <Container>
          <div className="text-center py-5">
            <h3>Invalid State</h3>
            <Button color="primary" onClick={handleBackToMap}>
              Back to Jobs Map
            </Button>
          </div>
        </Container>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <SEOTags 
        title={`${displayState} Construction Projects | ${displayState} Job Postings`}
        description={`Interactive map showing construction project postings in ${displayState}. Explore counties and cities with available positions and project opportunities.`}
        keywords={[`${displayState} construction projects`, `${state} job postings`, 'job map', 'local job opportunities']}
        schemaType="WebPage"
      />

      <Header isFullWidth={true} />

      <div className="page-content">
        <Container fluid className="px-4">
          <AutoBreadCrumb customTitle={`Jobs in ${displayState}`} />

          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <Button 
                color="outline-secondary" 
                size="sm" 
                className="me-3"
                onClick={handleBackToMap}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="me-1">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                </svg>
                Back to US Map
              </Button>
              <div>
                <h1 className="display-6 fw-semibold mb-0">
                  Jobs in {displayState}
                </h1>
              </div>
            </div>
            <p className="text-muted mb-0 fs-16">
              Explore job opportunities across counties and cities in {displayState}. 
              Click on any region to view detailed job listings.
            </p>
          </div>

          <InteractiveUSMap
            level="state"
            state={state}
            onRegionClick={handleRegionClick}
            height={600}
            className="mb-4"
          />
        </Container>
      </div>

      <Footer />
    </React.Fragment>
  );
};

export default JobStateMapPage;